import React, { useState } from 'react';
import {
  Users,
  Link as LinkIcon,
  RefreshCw,
  Copy,
  Check,
  XCircle,
  Plus,
  AlertCircle,
  Building2,
  Send,
  Trash2,
} from 'lucide-react';
import { usePartnerTokens, useRevokePartnerToken, useDeletePartnerToken, useGeneratePartnerToken } from '@/hooks/usePartnerTokens';
import { useCompanies } from '@/hooks/useCompanies';
import { usePageTour, WelcomeModal } from '@/walkthrough';
import { partnerManagementPageTour } from '@/walkthrough/tourSteps';
import { getMagicLinkUrl, resendPartnerInvite } from '@/services/partnerAdmin';
import type { PartnerToken, BusinessVisibility, Company } from '@/types';
import toast from 'react-hot-toast';

// ===========================================
// Generate Token Modal
// ===========================================

interface GenerateTokenModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function GenerateTokenModal({ isOpen, onClose }: GenerateTokenModalProps) {
  const { data: companies } = useCompanies();
  const generateMutation = useGeneratePartnerToken();

  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [businessVisibility, setBusinessVisibility] = useState<BusinessVisibility>('both');
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const selectedCompany = companies?.find((c) => c.id === selectedCompanyId);

  const handleGenerate = async () => {
    if (!selectedCompanyId || !selectedCompany) return;

    try {
      const result = await generateMutation.mutateAsync({
        companyId: selectedCompanyId,
        companyName: selectedCompany.name,
        businessVisibility,
      });

      if (result.magicLink) {
        setGeneratedLink(result.magicLink);
        toast.success('Access link generated successfully');
      }
    } catch (err) {
      toast.error('Failed to generate access link');
    }
  };

  const handleCopy = async () => {
    if (!generatedLink) return;
    await navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    toast.success('Link copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setSelectedCompanyId('');
    setBusinessVisibility('both');
    setGeneratedLink(null);
    setCopied(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Generate Partner Access Link</h2>
          <p className="text-sm text-gray-600 mt-1">
            Create a magic link for a partner to access their projects
          </p>
        </div>

        <div className="p-6 space-y-6">
          {!generatedLink ? (
            <>
              {/* Company Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Company (Referral Source)
                </label>
                <select
                  value={selectedCompanyId}
                  onChange={(e) => setSelectedCompanyId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a company...</option>
                  {companies?.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Business Visibility */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Visibility
                </label>
                <select
                  value={businessVisibility}
                  onChange={(e) => setBusinessVisibility(e.target.value as BusinessVisibility)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="both">Both (ACR & Blacksmith)</option>
                  <option value="azdisaster">ACR Only</option>
                  <option value="blacksmith">Blacksmith Only</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Partner will only see projects from the selected business(es)
                </p>
              </div>

              {/* Access Info */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600">
                  Access link will remain active until manually revoked. Partner can use it to view their projects anytime.
                </p>
              </div>
            </>
          ) : (
            <>
              {/* Generated Link Display */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-green-800 mb-2">
                  <Check size={20} />
                  <span className="font-medium">Access link generated!</span>
                </div>
                <p className="text-sm text-green-700 mb-4">
                  Copy and send this link to {selectedCompany?.name}
                </p>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={generatedLink}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  />
                  <button
                    onClick={handleCopy}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2"
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={18} />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium">Important:</p>
                    <p>This link provides access to all projects referred by this company. Only share it with authorized contacts.</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {generatedLink ? 'Done' : 'Cancel'}
          </button>
          {!generatedLink && (
            <button
              onClick={handleGenerate}
              disabled={!selectedCompanyId || generateMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {generateMutation.isPending ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <LinkIcon size={16} />
                  Generate Link
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ===========================================
// Token Row Component
// ===========================================

interface TokenRowProps {
  token: PartnerToken;
  company?: Company;
  onRevoke: (tokenId: string) => void;
  onDelete: (tokenId: string) => void;
  onSendEmail: (token: PartnerToken, company: Company) => void;
  isRevoking: boolean;
  isDeleting: boolean;
}

// Helper to parse Firestore timestamp or date string
function parseFirestoreDate(date: unknown): Date | null {
  if (!date) return null;

  // If it's already a Date
  if (date instanceof Date) return date;

  // If it's a Firestore Timestamp object (has _seconds or seconds)
  if (typeof date === 'object' && date !== null) {
    const obj = date as Record<string, unknown>;
    const seconds = obj._seconds ?? obj.seconds;
    if (typeof seconds === 'number') {
      return new Date(seconds * 1000);
    }
  }

  // If it's an ISO string or timestamp number
  if (typeof date === 'string' || typeof date === 'number') {
    const parsed = new Date(date);
    if (!isNaN(parsed.getTime())) return parsed;
  }

  return null;
}

function TokenRow({ token, company, onRevoke, onDelete, onSendEmail, isRevoking, isDeleting }: TokenRowProps) {
  const [copied, setCopied] = useState(false);

  // Token is active unless revoked (no expiration check)
  const isRevoked = !!token.revokedAt;
  const isActive = !isRevoked;

  const formatDate = (date: unknown) => {
    const parsed = parseFirestoreDate(date);
    if (!parsed) return 'N/A';
    return parsed.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const handleCopyLink = async () => {
    if (!token.token) return;
    const link = getMagicLinkUrl(token.token);
    await navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Link copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <tr className={isActive ? '' : 'opacity-60'}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Building2 size={16} className="text-gray-400" />
          <span className="font-medium text-gray-900">{token.companyName}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <span
          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
            isActive
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {isActive ? 'Active' : 'Revoked'}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {formatDate(token.createdAt)}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {token.lastUsedAt ? formatDate(token.lastUsedAt) : 'Never'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {token.useCount}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          {isActive && (
            <>
              {company?.email && (
                <button
                  onClick={() => onSendEmail(token, company)}
                  className="text-green-600 hover:text-green-800 text-sm font-medium flex items-center gap-1"
                >
                  <Send size={14} />
                  Email
                </button>
              )}
              {token.token && (
                <button
                  onClick={handleCopyLink}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? 'Copied!' : 'Copy Link'}
                </button>
              )}
              <button
                onClick={() => onRevoke(token.id)}
                disabled={isRevoking}
                className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center gap-1"
              >
                {isRevoking ? (
                  <RefreshCw size={14} className="animate-spin" />
                ) : (
                  <XCircle size={14} />
                )}
                Revoke
              </button>
            </>
          )}
          {isRevoked && (
            <button
              onClick={() => onDelete(token.id)}
              disabled={isDeleting}
              className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center gap-1"
            >
              {isDeleting ? (
                <RefreshCw size={14} className="animate-spin" />
              ) : (
                <Trash2 size={14} />
              )}
              Delete
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ===========================================
// Main Page Component
// ===========================================

export function PartnerManagementPage() {
  const { showWelcomeModal, startTour, skipTour, globalOptOut } = usePageTour(partnerManagementPageTour);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showRevoked, setShowRevoked] = useState(false);
  const [sendingEmailForToken, setSendingEmailForToken] = useState<string | null>(null);

  const { data: tokens, isLoading, refetch } = usePartnerTokens(undefined, showRevoked);
  const { data: companies } = useCompanies();
  const revokeMutation = useRevokePartnerToken();
  const deleteMutation = useDeletePartnerToken();

  // Create a map of company ID to company for quick lookup
  const companyMap = React.useMemo(() => {
    const map = new Map<string, Company>();
    companies?.forEach((c) => map.set(c.id, c));
    return map;
  }, [companies]);

  const handleRevoke = async (tokenId: string) => {
    if (!confirm('Are you sure you want to revoke this access link? The partner will no longer be able to access the portal.')) {
      return;
    }

    try {
      await revokeMutation.mutateAsync(tokenId);
      toast.success('Access link revoked');
    } catch {
      toast.error('Failed to revoke access link');
    }
  };

  const handleDelete = async (tokenId: string) => {
    if (!confirm('Are you sure you want to permanently delete this revoked access link? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteMutation.mutateAsync(tokenId);
      toast.success('Access link deleted');
    } catch {
      toast.error('Failed to delete access link');
    }
  };

  const handleSendEmail = async (token: PartnerToken, company: Company) => {
    if (!company.email) {
      toast.error('No email address on file for this company');
      return;
    }

    setSendingEmailForToken(token.id);
    try {
      // Use resend function to send email with existing token (does NOT create new token)
      await resendPartnerInvite(token.id, company.email, company.name);
      toast.success('Partner invitation email sent successfully');
    } catch (error: any) {
      const message = error?.message || 'Failed to send partner invitation';
      toast.error(message);
    } finally {
      setSendingEmailForToken(null);
    }
  };

  // Active = not revoked (no expiration check)
  const activeTokens = tokens?.filter((t) => !t.revokedAt) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Partner Management</h1>
          <p className="text-gray-600 mt-1">
            Manage partner portal access for referral sources
          </p>
        </div>
        <button
          onClick={() => setShowGenerateModal(true)}
          data-tour="generate-link"
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus size={18} />
          Generate Access Link
        </button>
      </div>

      {/* Info Card */}
      <div data-tour="partner-info" className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <LinkIcon className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="font-medium text-blue-900">Partner Portal Access</p>
            <p className="text-sm text-blue-800 mt-1">
              Partners can view their referred projects and send notes via magic link authentication.
              Each link is tied to a specific referral source company and can be revoked at any time.
            </p>
          </div>
        </div>
      </div>

      {/* Tokens Table */}
      <div data-tour="tokens-table" className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Users size={18} />
            Access Links ({activeTokens.length} active)
          </h2>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={showRevoked}
                onChange={(e) => setShowRevoked(e.target.checked)}
                className="rounded border-gray-300"
              />
              Show inactive
            </label>
            <button
              onClick={() => refetch()}
              className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
            >
              <RefreshCw size={14} />
              Refresh
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="p-8 text-center">
            <RefreshCw className="mx-auto text-gray-400 animate-spin" size={24} />
            <p className="text-gray-600 mt-2">Loading access links...</p>
          </div>
        ) : tokens && tokens.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Company
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Created
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Last Used
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Uses
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tokens.map((token) => (
                  <TokenRow
                    key={token.id}
                    token={token}
                    company={companyMap.get(token.companyId)}
                    onRevoke={handleRevoke}
                    onDelete={handleDelete}
                    onSendEmail={handleSendEmail}
                    isRevoking={revokeMutation.isPending}
                    isDeleting={deleteMutation.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center">
            <LinkIcon className="mx-auto text-gray-300" size={48} />
            <p className="text-gray-900 font-medium mt-4">No access links yet</p>
            <p className="text-gray-500 text-sm mt-1">
              Generate an access link to give a partner access to their projects
            </p>
            <button
              onClick={() => setShowGenerateModal(true)}
              className="mt-4 btn btn-primary inline-flex items-center gap-2"
            >
              <Plus size={18} />
              Generate First Link
            </button>
          </div>
        )}
      </div>

      {/* Generate Modal */}
      <GenerateTokenModal
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
      />

      <WelcomeModal
        pageName={partnerManagementPageTour.pageName}
        onStartTour={startTour}
        onSkip={skipTour}
        onGlobalOptOut={globalOptOut}
        open={showWelcomeModal}
      />
    </div>
  );
}
